import { useEffect, useState, type ReactNode } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Box, Target, Upload, Info } from "lucide-react";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { PageHeader } from "@/components/admin/shared/PageHeader";
import { PennField, PennInput, PennDropdown, PennButton } from "@/components/ui";
import {
  useCad3dSettingsAdmin,
  useUpdateCad3dSettingsAdmin,
} from "@/hooks/useCad3dSettingsAdmin";
import type { UpdateCad3dSettingsAdminBody } from "@/api/cad3dAdmin";

const cad3dSchema = z.object({
  tessellationMode: z.enum(["bbox_ratio", "absolute"]),
  bboxRatio: z.coerce.number().positive("BBox ratio must be greater than 0"),
  linearDeflectionMm: z.coerce.number().positive("Linear deflection must be greater than 0"),
  angularDeflection: z.coerce.number().positive("Angular deflection must be greater than 0"),
  matchThreshold: z.coerce
    .number()
    .min(0, "Threshold is between 0 and 1")
    .max(1, "Threshold is between 0 and 1"),
  bboxDiagGateRatio: z.coerce.number().positive("Gate ratio must be greater than 0"),
  maxFileBytes: z.coerce.number().int().positive("Max file size must be a positive integer"),
});
type Cad3dForm = z.infer<typeof cad3dSchema>;

const MODE_OPTIONS = [
  { value: "bbox_ratio", label: "bbox_ratio (recommended)" },
  { value: "absolute", label: "absolute" },
];

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

const formatMb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
const formatTimestamp = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};

export const Cad3dSettingsPage = () => {
  const [editOpen, setEditOpen] = useState(false);

  return (
    <>
      <PageHeader
        title="Dataset: CAD 3D Settings"
        subtitle="Whole-file JSON editor."
      />

      <div className="space-y-4">
        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-5 bg-white dark:bg-gray-900">
          <p className="text-blue_grey mb-1 text-body-semibold">
            Tessellation settings (affects GLB cache generation)
          </p>
          <p className="text-blue_grey3 mb-4 text-body">
            These values control how STEP geometry is tessellated when the server generates
            the cached GLB used by stepcheck/selector. Lower deflection values increase mesh
            quality but can significantly increase compute time and file size for parts with
            many faces.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-800 p-4">
              <p className="text-blue_grey mb-3 text-body-semibold">
                Recommended presets
              </p>
              <dl className="space-y-2 font-mono text-body">
                <PresetRow
                  label="Fast preview"
                  value="mode=bbox_ratio, bboxRatio=0.003, angularDeflection=1.4"
                />
                <PresetRow
                  label="Balanced"
                  value="mode=bbox_ratio, bboxRatio=0.001, angularDeflection=1.0"
                />
                <PresetRow
                  label="High detail"
                  value="mode=bbox_ratio, bboxRatio=0.0006, angularDeflection=0.7"
                />
              </dl>
            </div>

            <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-800 p-4">
              <p className="text-blue_grey mb-3 text-body-semibold">
                Fields
              </p>
              <ul className="space-y-2 text-blue_grey2 list-disc list-inside text-body">
                <li>
                  <code className="font-mono text-blue_grey">mode</code>:{" "}
                  <code className="font-mono">bbox_ratio</code> (recommended) or{" "}
                  <code className="font-mono">absolute</code>
                </li>
                <li>
                  <code className="font-mono text-blue_grey">bboxRatio</code>:
                  ratio of part bounding-box diagonal used as linear deflection
                </li>
                <li>
                  <code className="font-mono text-blue_grey">
                    linearDeflectionMm
                  </code>
                  : used only when <code className="font-mono">mode=absolute</code>
                </li>
                <li>
                  <code className="font-mono text-blue_grey">
                    angularDeflection
                  </code>
                  : larger values reduce mesh density (often faster)
                </li>
              </ul>
            </div>
          </div>

          <p className="text-blue_grey4 mt-4 text-small">
            Note: changing these values affects newly generated GLB caches. Existing GLBs
            stay unchanged unless you re-run "Analyze 3D".
          </p>
        </div>

        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-5 bg-white dark:bg-gray-900">
          <p className="text-blue_grey mb-3 text-body">
            Edit this dataset using a structured form.
          </p>
          <PennButton type="button" variant="primary" onClick={() => setEditOpen(true)}>
            Edit settings
          </PennButton>
        </div>
      </div>

      <Cad3dSettingsModal open={editOpen} onClose={() => setEditOpen(false)} />
    </>
  );
};

const PresetRow = ({ label, value }: { label: string; value: string }) => (
  <div className="grid grid-cols-[110px_1fr] gap-2">
    <dt className="text-penn-500 dark:text-penn-400 not-italic">{label}</dt>
    <dd className="text-blue_grey">{value}</dd>
  </div>
);

// =============================================================================
// Modal — matches the design screenshot: 4 sections, presets, R/O metadata.
// Theme tokens and layout primitives reused from the rest of admin (rounded
// cards, gray-50 backgrounds, PennField/Input/Dropdown).
// =============================================================================

const Cad3dSettingsModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { data: settings, isLoading, isError, error } = useCad3dSettingsAdmin(open);
  const updateMutation = useUpdateCad3dSettingsAdmin();

  const [formError, setFormError] = useState<string | null>(null);

  const { register, handleSubmit, reset, control, watch, formState: { errors, dirtyFields } } =
    useForm<Cad3dForm>({ resolver: zodResolver(cad3dSchema) });

  // Hydrate the form when the GET completes.
  useEffect(() => {
    if (settings) {
      reset({
        tessellationMode: (settings.tessellationMode === "absolute" ? "absolute" : "bbox_ratio"),
        bboxRatio: settings.bboxRatio ?? 0.00025,
        linearDeflectionMm: settings.linearDeflectionMm ?? 0.05,
        angularDeflection: settings.angularDeflection ?? 0.9,
        matchThreshold: settings.matchThreshold ?? 0.88,
        bboxDiagGateRatio: settings.bboxDiagGateRatio ?? 1.75,
        maxFileBytes: settings.maxFileBytes ?? 52428800,
      });
      setFormError(null);
    }
  }, [settings, reset]);

  // Reset transient state when the modal closes.
  useEffect(() => {
    if (!open) {
      setFormError(null);
    }
  }, [open]);

  const matchThreshold = watch("matchThreshold");
  const maxBytes = watch("maxFileBytes");

  const onSubmit = (form: Cad3dForm) => {
    setFormError(null);
    // Send only fields the user actually changed.
    const body: UpdateCad3dSettingsAdminBody = {};
    if (dirtyFields.tessellationMode) body.tessellationMode = form.tessellationMode;
    if (dirtyFields.bboxRatio) body.bboxRatio = form.bboxRatio;
    if (dirtyFields.linearDeflectionMm) body.linearDeflectionMm = form.linearDeflectionMm;
    if (dirtyFields.angularDeflection) body.angularDeflection = form.angularDeflection;
    if (dirtyFields.matchThreshold) body.matchThreshold = form.matchThreshold;
    if (dirtyFields.bboxDiagGateRatio) body.bboxDiagGateRatio = form.bboxDiagGateRatio;
    if (dirtyFields.maxFileBytes) body.maxFileBytes = form.maxFileBytes;

    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    updateMutation.mutate(body, {
      onSuccess: () => onClose(),
      onError: (err) => setFormError(apiErrorMessage(err, "Failed to save settings.")),
    });
  };

  const saveDisabled =
    isLoading || isError || !settings || updateMutation.isPending;

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="CAD 3D Settings"
      maxWidth="max-w-3xl"
      footer={
        <>
          <PennButton type="button" variant="secondary" onClick={onClose}>
            Discard
          </PennButton>
          <PennButton
            type="button"
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={saveDisabled}
          >
            {updateMutation.isPending ? "Saving…" : "Save changes"}
          </PennButton>
        </>
      }
    >
      <p className="text-blue_grey3 mb-4 text-body">
        Inline editor — changes apply to newly generated GLB caches.
      </p>

      {isLoading && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 px-4 py-3 text-blue_grey3 text-body">
          Loading settings…
        </div>
      )}

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load CAD 3D settings
          {error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      {!isLoading && !isError && settings && (
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
              {formError}
            </div>
          )}

          {/* Tessellation */}
          <Section title="Tessellation" icon={<Box size={14} />} tag="AFFECTS GLB CACHE">
            <p className="text-blue_grey3 mb-4 text-small">
              Controls how STEP geometry is meshed when the server generates the cached
              GLB used by stepcheck and the selector. Lower deflection values increase
              mesh quality but can significantly increase compute time and file size for
              parts with many faces.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <PennField
                label="mode"
                htmlFor="tessellationMode"
                error={errors.tessellationMode?.message}
                hint="Determines which deflection field is used."
              >
                <Controller
                  name="tessellationMode"
                  control={control}
                  render={({ field }) => (
                    <PennDropdown
                      inputId="tessellationMode"
                      name={field.name}
                      options={MODE_OPTIONS}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={!!errors.tessellationMode}
                    />
                  )}
                />
              </PennField>

              <FieldWithSuffix
                label="bboxRatio"
                htmlFor="bboxRatio"
                suffix="ratio"
                error={errors.bboxRatio?.message}
                hint="Fraction of bounding-box diagonal used as linear deflection."
              >
                <PennInput
                  id="bboxRatio"
                  type="number"
                  step="0.00001"
                  error={!!errors.bboxRatio}
                  {...register("bboxRatio")}
                />
              </FieldWithSuffix>

              <FieldWithSuffix
                label="linearDeflectionMm"
                htmlFor="linearDeflectionMm"
                suffix="mm"
                error={errors.linearDeflectionMm?.message}
                hint="Used only when mode is absolute."
              >
                <PennInput
                  id="linearDeflectionMm"
                  type="number"
                  step="0.01"
                  error={!!errors.linearDeflectionMm}
                  {...register("linearDeflectionMm")}
                />
              </FieldWithSuffix>

              <FieldWithSuffix
                label="angularDeflection"
                htmlFor="angularDeflection"
                suffix="rad"
                error={errors.angularDeflection?.message}
                hint="Larger values reduce mesh density (often faster)."
              >
                <PennInput
                  id="angularDeflection"
                  type="number"
                  step="0.1"
                  error={!!errors.angularDeflection}
                  {...register("angularDeflection")}
                />
              </FieldWithSuffix>
            </div>
          </Section>

          {/* Matching & gating */}
          <Section title="Matching & gating" icon={<Target size={14} />}>
            <p className="text-blue_grey3 mb-4 text-small">
              Thresholds used by stepcheck and the part selector when comparing geometry
              signatures.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldWithSuffix
                label="match_threshold"
                htmlFor="matchThreshold"
                suffix="0–1"
                error={errors.matchThreshold?.message}
                hint="Minimum similarity score to count as a match."
              >
                <div className="flex items-center gap-3">
                  <input
                    id="matchThreshold"
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    {...register("matchThreshold", { valueAsNumber: true })}
                    className="flex-1 accent-penn-500"
                  />
                  <span className="font-mono text-blue_grey tabular-nums w-12 text-right text-body">
                    {Number(matchThreshold ?? 0).toFixed(2)}
                  </span>
                </div>
              </FieldWithSuffix>

              <FieldWithSuffix
                label="bbox_diag_gate_ratio"
                htmlFor="bboxDiagGateRatio"
                suffix="×"
                error={errors.bboxDiagGateRatio?.message}
                hint="Reject candidates whose bbox diagonal differs by more than this factor."
              >
                <PennInput
                  id="bboxDiagGateRatio"
                  type="number"
                  step="0.05"
                  error={!!errors.bboxDiagGateRatio}
                  {...register("bboxDiagGateRatio")}
                />
              </FieldWithSuffix>
            </div>
          </Section>

          {/* Upload limits */}
          <Section title="Upload limits" icon={<Upload size={14} />}>
            <FieldWithSuffix
              label="max_file_bytes"
              htmlFor="maxFileBytes"
              suffix="bytes"
              error={errors.maxFileBytes?.message}
              hint="Reject STEP uploads larger than this value."
              right={
                <span className="text-blue_grey3 tabular-nums text-small">
                  {formatMb(Number(maxBytes || settings.maxFileBytes))}
                </span>
              }
            >
              <PennInput
                id="maxFileBytes"
                type="number"
                step="1048576"
                error={!!errors.maxFileBytes}
                {...register("maxFileBytes")}
              />
            </FieldWithSuffix>
          </Section>

          {/* System metadata (read-only) */}
          <Section title="System metadata" icon={<Info size={14} />} tag="READ-ONLY">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-body">
              <MetaPair label="id" value={settings.id} />
              <MetaPair label="signature_version" value={settings.signatureVersion} />
              <MetaPair label="created_at" value={formatTimestamp(settings.createdAt)} />
              <MetaPair label="updated_at" value={formatTimestamp(settings.updatedAt)} />
              <MetaPair label="created_by" value={settings.createdBy ?? "—"} />
              <MetaPair label="updated_by" value={settings.updatedBy ?? "—"} />
            </div>
          </Section>

          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/50 px-4 py-3 text-amber-800 dark:text-amber-200 text-body">
            Changing tessellation values affects newly generated GLB caches only. Existing
            GLBs stay unchanged until you re-run "Analyze 3D".
          </div>
        </form>
      )}
    </AdminModal>
  );
};

// ---------------------------------------------------------------------------

const Section = ({
  title,
  icon,
  tag,
  children,
}: {
  title: string;
  icon?: ReactNode;
  tag?: string;
  children: ReactNode;
}) => (
  <div className="border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden bg-white dark:bg-gray-900">
    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
      <div className="flex items-center gap-2">
        {icon && <span className="text-blue_grey3">{icon}</span>}
        <p className="text-blue_grey text-body-semibold">{title}</p>
      </div>
      {tag && (
        <span className="text-label uppercase text-blue_grey4">
          {tag}
        </span>
      )}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const FieldWithSuffix = ({
  label,
  htmlFor,
  suffix,
  hint,
  error,
  children,
  right,
}: {
  label: string;
  htmlFor: string;
  suffix?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  right?: ReactNode;
}) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label htmlFor={htmlFor} className="text-blue_grey2 font-mono text-small-medium">
        {label}
      </label>
      {(right ?? suffix) && (
        <span className="text-label uppercase text-blue_grey4">
          {right ?? suffix}
        </span>
      )}
    </div>
    {children}
    {error ? (
      <p className="mt-2 text-error text-small">{error}</p>
    ) : hint ? (
      <p className="mt-2 text-blue_grey4 text-small">{hint}</p>
    ) : null}
  </div>
);

const MetaPair = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-800 pb-2 last:border-b-0 last:pb-0">
    <span className="font-mono text-blue_grey3 truncate text-small">{label}</span>
    <span className="font-mono text-blue_grey tabular-nums truncate text-small">
      {value}
    </span>
  </div>
);
